import {getBorrowerBirthDate} from 'lib/borrower';
import {formatCurrency, formatPercent} from 'lib/currency';
import {useTranslation} from 'lib/i18n';
import {formatPaymentBreakdownValue, isPaymentBreakdownValueNegative} from 'lib/product';
import {parentheses} from 'lib/utils';
import {useMemo} from 'react';

export function useTierOverallResults(props = {}) {
  const {
    product: {
      calculation: {
        BorrowerProfile: {Age, DateOfBirth, Liens, ValueOfProperty} = {},
        PrincipalLimit,
        ClosingCost,
        Rate,
        ServiceFee,
        HomeSafeMetric: {OriginationFee} = {},
        SimplifiedCashToBorrower,
        InitialLoanBalance,
        InitialCashDraw,
        UnutilizedCash,
      } = {},
    } = {},
  } = props;
  const {t, cashLabel} = useTranslation();

  const isShortToClose = useMemo(() => {
    return SimplifiedCashToBorrower < 0 ? true : false;
  }, [SimplifiedCashToBorrower]);

  const availableCashSection = useMemo(
    () => [
      {
        label: t('illustratorTranslation:illustration.overall results.total proceeds'),
        tooltip: t('illustratorTranslation:illustration.overall results.override.tier.total proceeds tooltip'),
        value: formatCurrency(PrincipalLimit),
        order: 2,
        id: 'availableCashSection-total-proceeds',
        highlight: false,
      },
      {
        label: t('illustratorTranslation:illustration.overall results.total mortgages liens'),
        tooltip: t('illustratorTranslation:illustration.overall results.override.tier.total mortgages liens tooltip'),
        value: parentheses(formatCurrency(Liens)),
        useColorOptions: {value: 'red'},
        order: 3,
        id: 'availableCashSection-total-mortgage-balance-liens',
        highlight: false,
      },
      {
        label: t('illustratorTranslation:illustration.overall results.estimated closing costs'),
        tooltip: t('illustratorTranslation:illustration.overall results.override.tier.estimated closing costs tooltip'),
        value: parentheses(formatCurrency(ClosingCost)),
        useColorOptions: {value: 'red'},
        order: 4,
        id: 'availableCashSection-estimated-closing-cost',
        highlight: false,
      },
      {
        label: cashLabel(
          isShortToClose
            ? 'illustratorTranslation:illustration.overall results.cash due at close'
            : 'illustratorTranslation:illustration.overall results.available cash',
          {
            cash: SimplifiedCashToBorrower,
          },
        ),
        tooltip: t(
          isShortToClose
            ? 'illustratorTranslation:illustration.overall results.override.tier.cash due at close tooltip'
            : 'illustratorTranslation:illustration.overall results.override.tier.available cash tooltip',
        ),
        value: formatPaymentBreakdownValue(SimplifiedCashToBorrower),
        useColorOptions: {value: isPaymentBreakdownValueNegative(SimplifiedCashToBorrower) ? 'red' : undefined},
        order: 5,
        id: 'availableCashSection-available-cash',
        highlight: true,
      },
    ],
    [t, PrincipalLimit, Liens, ClosingCost, cashLabel, isShortToClose, SimplifiedCashToBorrower],
  );

  const initialLoanBalanceSection = useMemo(
    () => [
      {
        label: t('illustratorTranslation:illustration.overall results.mortgage liens payoff'),
        tooltip: t('illustratorTranslation:illustration.overall results.override.tier.mortgage liens payoff tooltip'),
        value: formatCurrency(Liens),
        order: 2,
        id: 'initialLoanBalanceSection-mortgage-liens-payoff',
        highlight: false,
      },
      {
        label: t('illustratorTranslation:illustration.overall results.estimated closing costs'),
        tooltip: t('illustratorTranslation:illustration.overall results.override.tier.estimated closing costs tooltip'),
        value: formatCurrency(ClosingCost),
        order: 3,
        id: 'initialLoanBalanceSection-estimated-closing-cost',
        highlight: false,
      },
      {
        label: cashLabel(
          isShortToClose
            ? 'illustratorTranslation:illustration.overall results.cash due at close'
            : 'illustratorTranslation:illustration.overall results.requested cash',
          {
            cash: InitialCashDraw,
          },
        ),
        tooltip: t(
          isShortToClose
            ? 'illustratorTranslation:illustration.overall results.override.tier.cash due at close tooltip'
            : 'illustratorTranslation:illustration.overall results.override.tier.requested cash tooltip',
        ),
        value: formatPaymentBreakdownValue(InitialCashDraw),
        useColorOptions: {value: isPaymentBreakdownValueNegative(InitialCashDraw) ? 'red' : undefined},
        order: 4,
        id: 'initialLoanBalanceSection-requested-cash',
        highlight: false,
      },
      {
        label: t('illustratorTranslation:illustration.overall results.initial loan balance'),
        tooltip: t('illustratorTranslation:illustration.overall results.override.tier.initial loan balance tooltip'),
        value: formatCurrency(InitialLoanBalance),
        order: 5,
        id: 'initialLoanBalanceSection-initial-loan-balance',
        highlight: true,
      },
      {
        label: cashLabel('illustratorTranslation:illustration.overall results.unutilized cash', {
          cash: UnutilizedCash,
        }),
        tooltip: t('illustratorTranslation:illustration.overall results.override.tier.unutilized cash tooltip'),
        value: formatCurrency(UnutilizedCash),
        order: 6,
        id: 'initialLoanBalanceSection-unutilized-cash',
        highlight: false,
      },
    ],
    [t, Liens, ClosingCost, cashLabel, isShortToClose, InitialCashDraw, InitialLoanBalance, UnutilizedCash],
  );

  const homeInformationSection = useMemo(
    () => [
      {
        label: t('illustratorTranslation:illustration.overall results.estimated home value'),
        tooltip: t('illustratorTranslation:illustration.overall results.override.tier.estimated home value tooltip'),
        value: formatCurrency(ValueOfProperty),
        order: 2,
        id: 'homeInformationSection-estimated-home-value',
      },
      {
        label: t('illustratorTranslation:illustration.overall results.current mortgage liens'),
        tooltip: t('illustratorTranslation:illustration.overall results.override.tier.current mortgage liens tooltip'),
        value: formatCurrency(Liens),
        order: 3,
        id: 'homeInformationSection-current-mortgage-liens',
      },
    ],
    [t, ValueOfProperty, Liens],
  );

  const youngestBorrowerSection = useMemo(
    () => [
      {
        label: t('illustratorTranslation:illustration.overall results.birth date'),
        tooltip: t('illustratorTranslation:illustration.overall results.override.tier.birth date tooltip'),
        value: getBorrowerBirthDate(DateOfBirth),
        order: 2,
        id: 'youngestBorrowerSection-birth-date',
      },
      {
        label: t('illustratorTranslation:illustration.overall results.nearest age'),
        tooltip: t('illustratorTranslation:illustration.overall results.override.tier.nearest age tooltip'),
        value: Age,
        order: 3,
        id: 'youngestBorrowerSection-nearest-age',
      },
    ],
    [t, DateOfBirth, Age],
  );

  const loanDetailsSection = useMemo(
    () => [
      {
        label: t('illustratorTranslation:illustration.overall results.rate'),
        tooltip: t('illustratorTranslation:illustration.overall results.override.tier.rate tooltip'),
        value: formatPercent(Rate),
        order: 2,
        id: 'loanDetailsSection-rate',
      },
      {
        label: t('illustratorTranslation:illustration.overall results.origination fee'),
        tooltip: t('illustratorTranslation:illustration.overall results.override.tier.origination fee tooltip'),
        value: formatPercent(OriginationFee, true, true),
        order: 3,
        id: 'loanDetailsSection-origination-fee',
      },
      {
        label: t('illustratorTranslation:illustration.overall results.service fee'),
        tooltip: t('illustratorTranslation:illustration.overall results.override.tier.service fee tooltip'),
        value: formatCurrency(ServiceFee),
        order: 4,
        id: 'loanDetailsSection-service-fee',
      },
    ],
    [t, Rate, OriginationFee, ServiceFee],
  );

  return {
    availableCashSection,
    initialLoanBalanceSection,
    homeInformationSection,
    youngestBorrowerSection,
    loanDetailsSection,
  };
}
